import { ClaimAmount } from '@/components/Claim/ClaimAmount'
import { ClaimSuccess } from '@/components/Claim/ClaimSuccess'
import { DownloadAppCta } from '@/components/Claim/DownloadAppCta'
import { NoAllocation } from '@/components/Claim/NoAllocation'
import { Layout } from '@/components/Layout'
import RewardsBreakdownWithErrorBoundary from '@/components/Rewards/RewardsBreakdown'
import { COUNTER_DURATION, SPRING_TRANSITION } from '@/constants/animations'
import { useAccountAllocationsData } from '@/hooks/useAccountAllocationsData'
import { useWalletAuth } from '@/hooks/useWalletAuth'
import { useWallets } from '@/hooks/useWallets'
import { AnimatePresence, motion } from 'framer-motion'
import { useRouter } from 'next/router'
// Create a context to track active polling for claim buttons
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'reshaped'

export const ClaimPollingContext = createContext<{
  isPolling: boolean
  isAnyClaiming: boolean
  setAnyClaiming: (claiming: boolean) => void
}>({
  isPolling: false,
  isAnyClaiming: false,
  setAnyClaiming: () => {},
})

export function useClaimPollingStatus() {
  return useContext(ClaimPollingContext)
}

const PAGE_TYPE = {
  LOADING: 'LOADING',
  NO_ALLOCATION: 'NO_ALLOCATION',
  CLAIMING: 'CLAIMING',
  SUCCESS: 'SUCCESS',
}

export default function ClaimPage() {
  const { ready, hasConnectedWallet } = useWalletAuth()
  const { wallets } = useWallets()
  const router = useRouter()
  const walletAddresses = wallets.map(([address]) => address)

  // Track animation state
  const [hasAnimatedRewards, setHasAnimatedRewards] = useState(false)

  // Track if we're actively polling for claim status
  const [isPolling, setIsPolling] = useState(false)

  // Track if any button is claiming (to disable others)
  const [isAnyClaiming, setAnyClaiming] = useState(false)

  // Primary page state
  const [pageType, setPageType] = useState(PAGE_TYPE.LOADING)

  // Timer ref to clean up on unmount
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Reference to the stop polling function from the hook
  const stopPollingRef = useRef<(() => void) | null>(null)

  const accounts = useAccountAllocationsData({
    identifiers: walletAddresses,
    onStartPolling: () => setIsPolling(true),
    onStopPolling: () => setIsPolling(false),
    setStopPollingRef: (fn) => {
      stopPollingRef.current = fn
    },
  })

  const totalClaimable = useMemo(() => {
    return Object.values(accounts || {}).reduce(
      (sum, account) => sum + (account?.totalTokens || 0),
      0
    )
  }, [accounts])

  const hasUnclaimedTokens = useMemo(() => {
    // Assume unclaimed if no accounts data yet
    if (!accounts || Object.keys(accounts).length === 0) return true

    // Go through each account and each wallet type to check for any unclaimed tokens
    return Object.values(accounts).some((account) => {
      if (
        (account.externalWallet?.tokens ?? 0) > 0 &&
        account.externalWallet?.claimStatus !== 'CLAIMED'
      ) {
        return true
      }

      if (
        (account.embeddedWallet?.tokens ?? 0) > 0 &&
        account.embeddedWallet?.claimStatus !== 'CLAIMED'
      ) {
        return true
      }

      if (
        (account.smartWallet?.tokens ?? 0) > 0 &&
        account.smartWallet?.claimStatus !== 'CLAIMED'
      ) {
        return true
      }

      return false
    })
  }, [accounts])

  // Calculate total claimed tokens across all wallets
  const totalClaimed = useMemo(() => {
    return Object.values(accounts || {}).reduce((sum, account) => {
      let accountClaimed = 0

      if (account.externalWallet?.claimStatus === 'CLAIMED') {
        accountClaimed += account.externalWallet.tokens
      }

      if (account.embeddedWallet?.claimStatus === 'CLAIMED') {
        accountClaimed += account.embeddedWallet.tokens
      }

      if (account.smartWallet?.claimStatus === 'CLAIMED') {
        accountClaimed += account.smartWallet.tokens
      }

      return sum + accountClaimed
    }, 0)
  }, [accounts])

  // Calculate total number of wallet allocations
  const totalAllocations = useMemo(() => {
    return Object.values(accounts || {}).reduce((count, account) => {
      let allocations = 0
      if ((account.externalWallet?.tokens ?? 0) > 0) allocations++
      if ((account.embeddedWallet?.tokens ?? 0) > 0) allocations++
      if ((account.smartWallet?.tokens ?? 0) > 0) allocations++
      return count + allocations
    }, 0)
  }, [accounts])

  // Determine if the user has multiple eligible allocations
  const hasMultipleAllocations = useMemo(() => {
    return totalAllocations > 1
  }, [totalAllocations])

  useEffect(() => {
    if (!accounts || Object.keys(accounts).length === 0) {
      return
    }

    if (totalClaimable === 0) {
      // No tokens to claim
      setPageType(PAGE_TYPE.NO_ALLOCATION)
    } else if (!hasUnclaimedTokens && totalClaimed > 0) {
      // All tokens are claimed and we have claimed tokens
      if (pageType === PAGE_TYPE.CLAIMING) {
        // Only apply a delay for multiple allocations
        if (hasMultipleAllocations) {
          // Clear any existing timer
          if (timerRef.current) {
            clearTimeout(timerRef.current)
          }

          // Apply a delay before showing success page
          // This gives users time to see all of their allocations are claimed
          timerRef.current = setTimeout(() => {
            setPageType(PAGE_TYPE.SUCCESS)
          }, 1500)
        } else {
          // For single allocation, transition immediately to success
          setPageType(PAGE_TYPE.SUCCESS)
        }
      } else if (pageType === PAGE_TYPE.LOADING) {
        // Immediate transition on load if all tokens already claimed
        setPageType(PAGE_TYPE.SUCCESS)
      }
    } else if (pageType === PAGE_TYPE.LOADING) {
      // Has tokens to claim - transition from loading to claiming
      setPageType(PAGE_TYPE.CLAIMING)
    }
  }, [
    accounts,
    totalClaimable,
    hasUnclaimedTokens,
    totalClaimed,
    pageType,
    hasMultipleAllocations,
  ])

  // Stop polling immediately when transitioning to success page
  useEffect(() => {
    if (pageType === PAGE_TYPE.SUCCESS && stopPollingRef.current) {
      stopPollingRef.current()
      setIsPolling(false)
    }
  }, [pageType])

  // Record when rewards animation completes
  const handleRewardsAnimationComplete = () => {
    setHasAnimatedRewards(true)
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (ready && !hasConnectedWallet) router.push('/')
  }, [ready, hasConnectedWallet, router])

  if (!ready || !hasConnectedWallet) {
    return null
  }

  // Define rewards breakdown once to prevent re-rendering issues
  const rewardsBreakdown = (
    <View align="center" width="100%">
      <View width="100%" paddingInline={pageType !== PAGE_TYPE.SUCCESS ? 4 : 0}>
        <RewardsBreakdownWithErrorBoundary
          walletAddresses={walletAddresses}
          isSuccess={pageType === PAGE_TYPE.SUCCESS}
          key={`rewards-${hasAnimatedRewards ? 'visible' : 'hidden'}`}
        />
      </View>
    </View>
  )

  return (
    <ClaimPollingContext.Provider value={{ isPolling, isAnyClaiming, setAnyClaiming }}>
      <Layout title="$ZORA Claim" claimSuccess={pageType === PAGE_TYPE.SUCCESS}>
        <View direction="column" justify="center" width="100%">
          <AnimatePresence mode="wait">
            {pageType === PAGE_TYPE.NO_ALLOCATION && (
              <motion.div
                key="no-allocation"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={SPRING_TRANSITION}
              >
                <NoAllocation />
              </motion.div>
            )}

            {pageType === PAGE_TYPE.SUCCESS && (
              <motion.div
                key="claim-success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={SPRING_TRANSITION}
              >
                <View direction="column" align="center" width="100%" gap={24}>
                  <DownloadAppCta />
                  <ClaimSuccess totalClaimed={totalClaimed} />
                  <View align="center" width="100%" paddingTop={4}>
                    {rewardsBreakdown}
                  </View>
                </View>
              </motion.div>
            )}

            {pageType === PAGE_TYPE.CLAIMING && (
              <motion.div
                key="claim-amount"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={SPRING_TRANSITION}
              >
                <View direction="column" align="center" width="100%" gap={8}>
                  <ClaimAmount />

                  {/* Rewards breakdown with delayed animation */}
                  <motion.div
                    key="rewards-animation"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      ...SPRING_TRANSITION,
                      delay: COUNTER_DURATION + 0.3,
                    }}
                    onAnimationComplete={handleRewardsAnimationComplete}
                    style={{ maxWidth: '100%' }}
                  >
                    {rewardsBreakdown}
                  </motion.div>
                </View>
              </motion.div>
            )}
          </AnimatePresence>
        </View>
      </Layout>
    </ClaimPollingContext.Provider>
  )
}
